import { Dispatch, FC, SetStateAction, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useThemeColors } from 'hooks/useThemeColors'

/* Utils */
import { FrameworkType, IChartData, IFramework, PerformanceStatus } from '../../../../../models'
import { PerformanceScoreTooltipProps } from './PerformanceScoreTooltip'
import { categoryToUrl } from '../../../library/utils'
import { buildLibraryLink } from '../../../library/library.filters'

/* Components */
import {
  EmptyPerformanceCategoriesWrapper,
  PerformanceCategoriesWrapper,
  PerformanceCategoryItem,
  PerformanceScoreWrapper
} from './performanceScore.styles'
import PerformanceScoreChart from './PerformanceScoreChart'
import { EmptyPizza } from '../../../../components/svg'

interface Props {
  chartLoading: boolean
  chartData: IChartData[]
  initialLoad: boolean
  noCategories: boolean
  isEmpty: boolean
  handleCategoryClick: (category: string) => void
  lastUpdatePerformance: string
  setHoveredCard: Dispatch<SetStateAction<string>>
  customFramework: IFramework | null
}

const PerformanceScore: FC<Props> = ({
  chartLoading,
  chartData,
  initialLoad,
  isEmpty,
  handleCategoryClick,
  noCategories,
  lastUpdatePerformance,
  setHoveredCard,
  customFramework
}) => {
  const getColor = useThemeColors()
  const navigate = useNavigate()
  const [tooltip, setTooltip] = useState<PerformanceScoreTooltipProps>({ x: 0, y: 0, content: null })
  const [overperformanceIconTooltip, setOverperformanceIconTooltip] = useState<PerformanceScoreTooltipProps>({
    x: 0,
    y: 0,
    content: null
  })

  const handleCategoryItemClick = (cat: string) => {
    const url = categoryToUrl(cat)
    navigate(`/library/categories/${url}`)
  }

  const handleLibraryCategoryRedirect = (cat: string) => {
    navigate(buildLibraryLink(cat))
  }

  const handleFrameworkRedirect = (cat: string) => {
    const frameworkVariant = (customFramework && customFramework.variant) || FrameworkType.Custom
    navigate(`/framework-configuration/${frameworkVariant}`, { state: { navigatedCategory: cat } })
  }

  const data = [...chartData.map((n) => ({ ...n, score: n.score === 100 ? 99.99 : n.score }))]
  if (data.length < 6) {
    data.push({ category: '', emptySlice: true, status: PerformanceStatus.unavailable, score: 0 })
  }

  return (
    <PerformanceScoreWrapper isempty={isEmpty ? 'true' : 'false'} className="category-performance-score-wrapper">
      <Box className="title-wrapper">
        <Box className="title-block">
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M1.5 12L6 7.49999L8.87095 10.3709C9.67341 8.79207 11.0028 7.46819 12.7468 6.69169L14.5739 5.87821M14.5739 5.87821L10.6131 4.35779M14.5739 5.87821L13.0535 9.83906"
              stroke={getColor('menu-item-text')}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <Typography className="chart-title">Program Score by Category</Typography>
        </Box>

        <Typography className="date">
          Last Update: <span>{lastUpdatePerformance}</span>
        </Typography>
      </Box>

      <Box className="pizza-chart-container">
        <Box className="pizza-chart-wrapper">
          {!chartLoading && chartData.length > 0 && !isEmpty && (
            <PerformanceScoreChart
              // ONY-2326 Show only available CPIs with 1 "Coming Soon" slice
              // ONY-3272 Show inactive slice only when 5 or fewer slices are shown on pizza chart
              data={data}
              noCategory={initialLoad && noCategories}
              tooltip={tooltip}
              setTooltip={setTooltip}
              overperformanceIconTooltip={overperformanceIconTooltip}
              setOverperformanceIconTooltip={setOverperformanceIconTooltip}
              handleCategoryClick={handleCategoryClick}
              setHoveredCard={setHoveredCard}
            />
          )}
          {isEmpty && <EmptyPizza />}
        </Box>

        {isEmpty && (
          <EmptyPerformanceCategoriesWrapper>
            <Box className="category-row">
              <Typography className="name">Threat Detection</Typography>
              <Typography className="value">%</Typography>
            </Box>
            <Box className="category-row">
              <Typography className="name">Vulnerability Management</Typography>
              <Typography className="value">%</Typography>
            </Box>
            <Box className="category-row">
              <Typography className="name">Traininig & Awareness</Typography>
              <Typography className="value">%</Typography>
            </Box>
            <Box className="category-row">
              <Typography className="name">Cloud Security</Typography>
              <Typography className="value">%</Typography>
            </Box>
          </EmptyPerformanceCategoriesWrapper>
        )}

        {!isEmpty && (
          <PerformanceCategoriesWrapper className="performance-categories-list-wrapper">
            <Box className="performance-categories-inner">
              {data.map(({ category, status, score }) => {
                if (!category) return null
                return (
                  <PerformanceCategoryItem
                    key={category}
                    onClick={() => {
                      if (status === PerformanceStatus.available) {
                        handleLibraryCategoryRedirect(category)
                      } else if (status === PerformanceStatus.unavailable) {
                        handleFrameworkRedirect(category)
                      } else {
                        handleCategoryItemClick(category)
                      }
                    }}
                    onMouseEnter={() => setHoveredCard(category)}
                    onMouseLeave={() => setHoveredCard('')}
                    isselected={tooltip.content && tooltip.content.category === category ? 'true' : 'false'}
                  >
                    <Typography className="name">{category}</Typography>
                    {status === PerformanceStatus.available ? (
                      <Typography className="explore-category">Explore CPI Library</Typography>
                    ) : status === PerformanceStatus.unavailable ? (
                      <Typography className="explore-category">Add CPIs</Typography>
                    ) : (
                      <Typography className="value">{Math.round(score)}%</Typography>
                    )}
                  </PerformanceCategoryItem>
                )
              })}
            </Box>
          </PerformanceCategoriesWrapper>
        )}
      </Box>
    </PerformanceScoreWrapper>
  )
}

export default PerformanceScore
